import { useCallback } from 'react';
import { useToki } from '../context/TokiContext';
import { useHambi } from '../context/HambiContext';

/**
 * Unified super-app hook — single source of truth for auth + UI flags.
 *
 * Combines TokiContext and HambiContext so consumers never need to
 * manually combine 4 separate flags or copy-paste retry logic.
 *
 * Auth consumers:  isSuperAppUser, isAuthenticated, isAuthLoading, currentUser, retryAuth
 * UI consumers:    isInNativeApp, nativeApp ('toki' | 'hambi' | null)
 */
export function useSuperApp() {
  const toki = useToki();
  const hambi = useHambi();

  // === AUTH ===
  // "Is this user from ANY super-app?" — covers all 4 sources:
  //   Toki WebView bridge, stored Toki session, Hambi URL locale, stored Hambi session
  const isSuperAppUser = toki.isInTokiApp || toki.isTokiUser
                       || hambi.isInHambiApp || hambi.isHambiUser;

  const isAuthenticated = toki.isAuthenticated || hambi.isAuthenticated;
  const isAuthLoading = toki.isLoading || hambi.isLoading;

  // Priority: Toki > Hambi (matches existing pattern across codebase)
  const currentUser = toki.currentUser || hambi.user;

  // === UI ===
  // "Are we inside a native WebView right now?" — for layout adjustments (padding, safe areas)
  const isInNativeApp = toki.isInTokiApp || hambi.isInHambiApp;

  // Which specific app for UI-only cases (Toki needs 40px top padding; Hambi needs close button)
  const nativeApp = toki.isInTokiApp ? 'toki' : hambi.isInHambiApp ? 'hambi' : null;

  // === UNIFIED RETRY ===
  // Single function that tries Toki first, then Hambi. Returns true if re-auth succeeded.
  const retryAuth = useCallback(async () => {
    if ((toki.isInTokiApp || toki.isTokiUser) && toki.retryAuth) {
      const success = await toki.retryAuth();
      if (success) return true;
    }
    if ((hambi.isInHambiApp || hambi.isHambiUser) && hambi.retryAuth) {
      const success = await hambi.retryAuth();
      if (success) return true;
    }
    return false;
  }, [toki.isInTokiApp, toki.isTokiUser, toki.retryAuth,
      hambi.isInHambiApp, hambi.isHambiUser, hambi.retryAuth]);

  return {
    // Auth
    isSuperAppUser,
    isAuthenticated,
    isAuthLoading,
    currentUser,
    retryAuth,

    // UI
    isInNativeApp,
    nativeApp,
  };
}

export default useSuperApp;
